import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import PaginationComponent from '../../Pagination';
import NoDataComponent from '../../NoData';
import TableComponent from '../../Table';
import InputSearchComponent from '../../Common/Form/input-search';
import { getReceivedAwardPointHistory } from '../../../apis/article.api';
import { formatDate } from '../../../utils/helper.util';
import ImageComponent from '../../Common/Image';
import ChevronDownIcon from '../../../assets/chevron-down-green-icon.svg';
import { useNavigate } from 'react-router-dom';
import PointIcon from '../../../assets/point-icon.svg';
import CommentIcon from '../../../assets/comment-icon.svg';
import AvatarIcon from '../../../assets/medium_default_optimized.png';
import NoResultImg from '../../../assets/no-result.svg';

const points = [1, 2, 3, 4, 5];

const listContentType = [
  {
    name: 'Article',
    value: 'article'
  },
  {
    name: 'Comment',
    value: 'comment'
  }
];

export const ReceivedPointTableComponent = () => {
  const navigate = useNavigate();
  const [dataTable, setDataTable] = useState([]);
  const [pagination, setPagination] = useState({
    limit: 4,
    page: 1,
    sortBy: undefined,
    sort: undefined,
    search: undefined
  });
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [contentType, setContentType] = useState({});

  const params = {
    ...pagination,
    entityTypes: [contentType.value] || undefined,
    points: selectedPoints
  };

  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined)
  );

  const { data: receivedAwardPointHistory, isFetching } = useQuery({
    queryKey: ['getReceivedAwardPointHistory', filteredParams],
    queryFn: () => getReceivedAwardPointHistory(filteredParams)
  });

  const handleChangePage = (page) => {
    setDataTable(() => []);
    setPagination((pre) => {
      return {
        ...pre,
        page
      };
    });
  };

  const handleChangeSearch = (search) => {
    setPagination((pre) => {
      return {
        ...pre,
        page: 1,
        search: search
      };
    });
  };

  const handleChangeType = (name, value) => () => {
    setContentType({ name: name, value: value });
    setPagination((pre) => {
      return {
        ...pre,
        page: 1
      };
    });
  };

  const togglePoint = (point) => {
    // Prevent the MenuItem from closing the dropdown
    setSelectedPoints((prev) =>
      prev.includes(point) ? prev.filter((p) => p !== point) : [...prev, point]
    );
    setPagination((pre) => ({
      ...pre,
      page: 1
    }));
  };

  const convertPoints = useCallback((row) => {
    return (
      <Box display={'flex'} alignItems={'center'} gap={1}>
        <img src={PointIcon} className="w-4 h-4" alt="" />
        <Text fontWeight={800} color={'#204A22'} fontSize={'xs'}>
          {row?.amount === 1 ? `${row?.amount} Twig` : `${row?.amount} Twigs`}
        </Text>
      </Box>
    );
  }, []);

  const convertReceivedOn = useCallback(
    (row) => {
      return (
        <Box>
          {row.entityType === 'article' ? (
            <Box
              display={'flex'}
              alignItems={'center'}
              width={{ base: '100vw', md: 'unset' }}
              cursor={'pointer'}
              onClick={() => navigate(`/articles/${row?.entityId}`)}
            >
              <Box position={'relative'} flexShrink={0}>
                {row?.cover ? (
                  <ImageComponent
                    rounded={'12px'}
                    w={'40px'}
                    h={'40px'}
                    src={row?.cover}
                    alt="cover"
                  />
                ) : (
                  <Box
                    rounded={'12px'}
                    w={'40px'}
                    h={'40px'}
                    bgColor={'#F2F3F7'}
                    display={'flex'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    textAlign={'center'}
                    fontSize={'10px'}
                    lineHeight={'100%'}
                  >
                    No Image
                  </Box>
                )}
              </Box>
              <Box ml={'12px'} display={'flex'}>
                <Text
                  fontWeight={800}
                  fontSize={'xs'}
                  title={row?.title}
                  overflow={'hidden'}
                  textOverflow={'ellipsis'}
                  wordBreak={'break-word'}
                  sx={{
                    display: '-webkit-inline-box',
                    WebkitLineClamp: '1',
                    WebkitBoxOrient: 'vertical'
                  }}
                  _hover={{
                    textDecoration: 'underline'
                  }}
                >
                  {row?.title || 'Untitled'}
                </Text>
              </Box>
            </Box>
          ) : (
            <Box
              display={'flex'}
              alignItems={'center'}
              width={{ base: '100vw', md: 'unset' }}
              cursor={'pointer'}
              onClick={() => navigate(`/articles/${row.commentTargetArticleId}#general-comment`)}
              p={'10px'}
            >
              <img src={CommentIcon} className="w-4 h-4" alt="" />
              <Text fontSize={'xs'} fontWeight={400} ml={1}>
                Comment on article:{' '}
                <Text
                  as={'span'}
                  fontWeight={800}
                  _hover={{
                    textDecoration: 'underline'
                  }}
                >
                  {row?.title}
                </Text>
              </Text>
            </Box>
          )}
        </Box>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const convertContentType = useCallback((row) => {
    return (
      <Box
        key={row.id}
        px={'8px'}
        py={'4px'}
        rounded={'80px'}
        bgColor={'#F0EDEC'}
        w={'fit-content'}
        whiteSpace={'nowrap'}
      >
        <Text
          title={row?.entityType}
          color="#6A4E42"
          fontWeight={800}
          fontSize={'xs'}
          textTransform={'capitalize'}
        >
          {row?.entityType}
        </Text>
      </Box>
    );
  }, []);

  const convertFrom = useCallback((row) => {
    return (
      <Box
        display={'flex'}
        alignItems={'center'}
        cursor={'pointer'}
        gap={2}
        onClick={() => navigate(`/${row?.fromUser?.username}`)}
      >
        <img
          src={row?.fromUser?.imgUrl || AvatarIcon}
          className="w-6 h-6 rounded-full object-cover flex-shrink-0"
          alt=""
        />
        <Box display={'flex'} flexDirection={'column'} minW={'0'}>
          <Text
            fontSize={'xs'}
            fontWeight={600}
            _hover={{
              color: '#4CAF50'
            }}
            transition={'0.2s'}
            title={row?.fromUser?.firstName}
            isTruncated
          >
            {row?.fromUser?.firstName}
          </Text>
          {row?.fromUser?.username && (
            <span className="font-[600] text-[10px] leading-[12px] text-02-dim-gray">
              @{row?.fromUser?.username}
            </span>
          )}
        </Box>
      </Box>
    );
  }, []);

  const handleSort = (sortName) => {
    let objSort = {
      sortBy: pagination.sortBy,
      sort: pagination.sort
    };
    if (sortName !== objSort.sortBy) objSort = { sortBy: sortName, sort: 'asc' };
    else if (sortName === objSort.sortBy && objSort.sort === 'asc')
      objSort = {
        ...objSort,
        sort: 'desc'
      };
    else if (sortName === objSort.sortBy && objSort.sort === 'desc')
      objSort = {
        ...objSort,
        sort: 'asc'
      };
    setPagination({ ...pagination, ...objSort });
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Twigs',
        accessor: (row) => convertPoints(row),
        id: 'amount',
        isSortField: true,
        widthCustom: '12rem',
        minWidthCustom: '12rem'
      },
      {
        Header: 'Received on',
        accessor: (row) => convertReceivedOn(row),
        id: 'title',
        isSortField: true,
        widthCustom: 'calc(100% - 35.5rem)',
        minWidthCustom: '20rem'
      },
      {
        Header: 'Content type',
        accessor: (row) => convertContentType(row),
        id: 'contentType',
        widthCustom: '12rem',
        minWidthCustom: '12rem'
      },
      {
        Header: 'From',
        accessor: (row) => convertFrom(row),
        id: 'from_user',
        widthCustom: '10rem',
        minWidthCustom: '10rem',
        isSortField: true
      },
      {
        Header: 'Date Received',
        accessor: (row) => <Text fontSize={'xs'}>{formatDate(row?.createdAt, true)}</Text>,
        id: 'created_at',
        widthCustom: '14rem',
        minWidthCustom: '14rem',
        isSortField: true
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [convertReceivedOn, convertContentType, handleSort]
  );

  useEffect(() => {
    if (receivedAwardPointHistory) {
      setDataTable(receivedAwardPointHistory?.data);
    }
  }, [receivedAwardPointHistory, contentType.value, selectedPoints]);

  return (
    <Box
      w={'full'}
      border={'1px solid'}
      borderColor={'anti-flash-lite'}
      borderRadius={{ base: 0, md: '12px' }}
      shadow={'outline'}
    >
      <Box
        bg={'white'}
        p={{ base: '16px', md: '16px 24px' }}
        display={'flex'}
        alignItems={'start'}
        minH={'72px'}
        borderTopRadius={'12px'}
        justifyContent={'space-between'}
        overflow={'auto'}
      >
        <Box
          display={'flex'}
          gap="12px"
          flexDirection={{ base: 'column', md: 'row' }}
          width={'full'}
          overflow={'auto'}
        >
          <InputSearchComponent
            setPaginationSearch={handleChangeSearch}
            placeholder={'Search'}
            setLoading={() => {}}
            width={{ base: '100%', md: '320px' }}
          />
          <div className="flex items-center gap-3">
            <Menu closeOnSelect={false}>
              <MenuButton
                as={Button}
                rightIcon={<img src={ChevronDownIcon} alt="icon" />}
                height={'36px'}
                w={{ base: '100%', md: 'fit-content' }}
                variant={'growth-green-solid'}
                px={'18px'}
              >
                <Text color={'#4CAF50'} fontWeight={900} textAlign={'start'} fontSize={'sm'}>
                  Number of Twigs {selectedPoints.length > 0 ? `(${selectedPoints.length})` : ''}
                </Text>
              </MenuButton>
              <MenuList
                p={'0.5rem'}
                borderRadius={'0.5rem'}
                minW={'117px'}
                shadow={'md'}
                border={'1px solid'}
                borderColor={'light-gray'}
                maxH={'300px'}
                overflow={'auto'}
                css={{
                  '&::-webkit-scrollbar': {
                    width: '6px'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#e6e7eb',
                    borderRadius: '30px'
                  }
                }}
              >
                {points?.map((point, index) => {
                  return (
                    <MenuItem
                      key={index}
                      onClick={() => togglePoint(point)}
                      p={'8px 8px 8px 10px'}
                      _focus={{ bgColor: 'white' }}
                      _hover={{
                        bgColor: 'anti-flash-white',
                        '& .child-box': {
                          display: 'block'
                        }
                      }}
                      rounded={'0.5rem'}
                      fontSize={'14px'}
                      lineHeight={'20px'}
                      fontWeight={'800'}
                    >
                      <Checkbox
                        isChecked={selectedPoints.includes(point)}
                        onChange={() => togglePoint(point)}
                      >
                        {point}
                      </Checkbox>
                    </MenuItem>
                  );
                })}
              </MenuList>
            </Menu>
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<img src={ChevronDownIcon} alt="icon" />}
                w={{ base: '100%', md: 'fit-content' }}
                height={'36px'}
                variant={'growth-green-solid'}
                px={'18px'}
              >
                <Text color={'#4CAF50'} fontWeight={900} fontSize={'sm'} textAlign={'start'}>
                  {!contentType.name ? 'Content Type' : contentType.name}
                </Text>
              </MenuButton>
              <MenuList
                p={'0.5rem'}
                borderRadius={'0.5rem'}
                minW={'171px'}
                maxH={'300px'}
                overflow={'auto'}
                css={{
                  '&::-webkit-scrollbar': {
                    width: '6px'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#e6e7eb',
                    borderRadius: '30px'
                  }
                }}
                shadow={'md'}
                border={'1px solid'}
                borderColor={'light-gray'}
              >
                {[{ name: 'All Types', value: undefined }, ...(listContentType || [])]?.map(
                  (item, index) => {
                    return (
                      <MenuItem
                        key={index}
                        onClick={handleChangeType(item.name, item.value)}
                        p={'8px 8px 8px 10px'}
                        _focus={{ bgColor: 'white' }}
                        _hover={{
                          bgColor: 'anti-flash-white',
                          '& .child-box': {
                            display: 'block'
                          }
                        }}
                        rounded={'0.5rem'}
                        fontSize={'14px'}
                        lineHeight={'20px'}
                        fontWeight={'800'}
                      >
                        {item.name}
                      </MenuItem>
                    );
                  }
                )}
              </MenuList>
            </Menu>
            {selectedPoints.length > 0 && (
              <div className="font-[800] text-sm">
                Filtered by Twigs: {selectedPoints.map((point) => point).join(', ')}
              </div>
            )}
          </div>
        </Box>
      </Box>
      <Box
        bg={'white'}
        rounded={'0 0 0.75rem 0.75rem'}
        display={'flex'}
        minH={{ base: '400px', md: '450px' }}
        flexFlow={'column'}
        overflowX={'auto'}
        css={{
          '&::-webkit-scrollbar': {
            display: 'block',
            height: '8px' // For horizontal scrollbar
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#e6e7eb',
            borderRadius: '30px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f5f5f5'
          }
        }}
      >
        <TableComponent
          isLoading={isFetching}
          columns={columns}
          data={dataTable ?? []}
          headerHeight={'44px'}
          fontsizeTable={'sm'}
          handleSort={handleSort}
          currentSort={{ sortBy: pagination.sortBy, sort: pagination.sort }}
          haveFooter={false}
        />
        {!pagination.search && dataTable?.length === 0 && !isFetching ? (
          <Box
            minH={{ base: '400px', md: '450px' }}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <NoDataComponent
              title="It looks like you haven’t received any Twigs yet."
              description="Keep writing articles and comments, to get more recognition!"
              onClick={() => navigate('/')}
              isFetching={isFetching}
            />
          </Box>
        ) : null}
        {pagination.search && dataTable?.length === 0 && !isFetching ? (
          <Box
            minH={{ base: '400px', md: '450px' }}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <NoDataComponent
              icon={NoResultImg}
              title="No results found"
              description="Try a different search term"
              isFetching={isFetching}
            />
          </Box>
        ) : null}
      </Box>

      {receivedAwardPointHistory?.totalPage > 0 ? (
        <Box
          borderBottomRadius={'12px'}
          p={'1.25rem 1.5rem'}
          mt={'auto'}
          borderTop="1px solid"
          borderColor={'anti-flash-lite'}
          background={'light.100'}
        >
          <PaginationComponent
            currentPage={pagination.page}
            totalPages={receivedAwardPointHistory?.totalPage}
            onChangePage={handleChangePage}
          />
        </Box>
      ) : null}
    </Box>
  );
};
